import { useEffect, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { trackEvent } from '@/utils/analytics/useAnalytics'

import {
  Flex as DarkFlex,
  Tabs as DarkTabs,
  TabItem,
  tokenPrefix,
} from '@hivewatch/dark'

import { useAppSelector } from '@/redux/hooks'
import { selectIncidentTraceLogDrawerState } from '@/redux/ui/uiSlice'
import { BUTTON_PRESS } from '@/utils/analytics'

import { EventFiltersI } from '../../../shared/types/traceLogTypes'
import {
  DEVICES_TAB_EVENT,
  FACILITIES_TAB_EVENT,
  INCIDENT_TAB_EVENT,
  getIsTailgatingIncident,
  getMixpanelEventData,
} from '../../../shared/utils/traceLogUtils'
import { EventFeed } from './EventFeed'
import { TraceLogHeader } from './TraceLogHeader'

type TraceLogTabId = 'incident' | 'device' | 'facility'

const TAB_EVENT_BY_ID: Record<TraceLogTabId, string> = {
  incident: INCIDENT_TAB_EVENT,
  device: DEVICES_TAB_EVENT,
  facility: FACILITIES_TAB_EVENT,
}

// Deep-link intent expressed as a tab id rather than an index — the Incident
// tab is conditional, so an index would change meaning when it comes or goes.
const getDefaultTabId = (
  shouldForceDeviceTab: boolean,
  shouldShowIncidentTab: boolean
): TraceLogTabId | undefined => {
  if (shouldForceDeviceTab) return 'device'
  if (shouldShowIncidentTab) return 'incident'
  return undefined
}

export const TraceLogContent = ({ incident }: EventFiltersI) => {
  const { highlightedEvent, deviceId, shouldForceDeviceTab } = useAppSelector(
    selectIncidentTraceLogDrawerState
  )
  const isIncidentTypeDefinition = useFeatureFlag(
    FeatureFlag.useIncidentTypeDefinition
  )
  const mode = useLightMode()

  // Show Incident tab for Tailgating incidents with associated events,
  // or when opened from a resolution EVENT_REF click. Not for the Reasoning
  // Trail device deep-link: it sets `highlightedEvent` for the Device-tab
  // highlight, but its event is device-scoped, so an Incident tab would be a
  // spurious (usually empty) panel.
  const isTailgatingIncident = getIsTailgatingIncident(
    incident,
    isIncidentTypeDefinition
  )
  const hasIncidentEvents =
    isTailgatingIncident && incident?.eventExternalIds?.length > 0
  const shouldShowIncidentTab =
    !shouldForceDeviceTab && (hasIncidentEvents || !!highlightedEvent)

  // Device the Device tab filters to. The Reasoning Trail deep-link supplies a
  // specific device; otherwise fall back to the incident's primary device.
  const deviceFilterId = deviceId ?? incident?.devices?.[0]?.id

  // Controlled selection: the drawer content stays mounted, so the default
  // alone won't switch tabs when a new deep-link updates Redux.
  const [activeTabId, setActiveTabId] = useState<TraceLogTabId>(
    getDefaultTabId(shouldForceDeviceTab, shouldShowIncidentTab) ?? 'device'
  )

  // Re-select the intended tab when the deep-link intent changes. `deviceId`
  // and the highlighted event's identity are deps so a fresh link — to a
  // different device or a different event — re-asserts its target tab even
  // when the drawer is already open on another tab.
  useEffect(() => {
    const target = getDefaultTabId(shouldForceDeviceTab, shouldShowIncidentTab)
    if (target !== undefined) {
      setActiveTabId(target)
    }
  }, [
    shouldForceDeviceTab,
    shouldShowIncidentTab,
    deviceId,
    highlightedEvent?.externalId,
  ])

  const handleMixPanelEvent = (tabName: string) => {
    trackEvent(
      `${BUTTON_PRESS} ${tabName} tab - trace logs`,
      getMixpanelEventData(incident)
    )
  }

  const prefix = tokenPrefix(mode)
  const items: TabItem[] = [
    ...(shouldShowIncidentTab ? [{ id: 'incident', label: 'Incident' }] : []),
    { id: 'device', label: 'Device' },
    { id: 'facility', label: 'Facility' },
  ]
  // A selected Incident tab falls back to Device if the tab disappears
  // (e.g. the highlight is cleared) instead of silently becoming Facility.
  const activeId: TraceLogTabId =
    activeTabId === 'incident' && !shouldShowIncidentTab
      ? 'device'
      : activeTabId

  const handleTabChange = (id: string) => {
    if (items.some((item) => item.id === id)) {
      setActiveTabId(id as TraceLogTabId)
    }
    // Guarded lookup: a tab id without a mapped event name must not track
    // a malformed "undefined tab" Mixpanel event.
    const tabEvent = TAB_EVENT_BY_ID[id as TraceLogTabId] as string | undefined
    if (tabEvent) {
      handleMixPanelEvent(tabEvent)
    }
  }

  return (
    <DarkFlex direction='column' flex='1 1 0%' minH={0}>
      <TraceLogHeader />
      <DarkTabs
        activeId={activeId}
        colorScheme={mode}
        items={items}
        onChange={handleTabChange}
        tabListStyleOverrides={{
          borderBottomColor: `${prefix}border.subtle`,
          flexShrink: 0,
          gap: '8px',
          px: '24px',
        }}
        tabStyleOverrides={{
          flex: '1 0 0',
          fontSize: '14px',
          h: '40px',
          justifyContent: 'center',
          px: 0,
        }}
      />
      {/* Keys remount the feed when its identity changes — useEvents only
          installs results while its cursor is null, so a mounted feed would
          otherwise keep the previous incident/device/highlight's events. */}
      {activeId === 'incident' && (
        <EventFeed
          filters={{ deviceId: incident?.devices?.[0]?.id }}
          incident={incident}
          key={`incident-${incident?.id}`}
          showOnlyIncidentRelatedEvents={true}
        />
      )}
      {activeId === 'device' && (
        <EventFeed
          filters={{ deviceId: deviceFilterId }}
          incident={incident}
          key={`device-${incident?.id}-${deviceFilterId}-${
            highlightedEvent?.detectedAt ?? ''
          }`}
        />
      )}
      {activeId === 'facility' && (
        <EventFeed
          filters={{ facilityId: incident?.facility?.id }}
          incident={incident}
          key={`facility-${incident?.id}-${incident?.facility?.id}-${
            highlightedEvent?.detectedAt ?? ''
          }`}
        />
      )}
    </DarkFlex>
  )
}
